import axios from 'axios';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { getAPIErrorReason } from '../helpers/common';
import { CUSTOMER_MEMBER_ROLE } from '../global/constants';
import {
  CustomerBillableHoursDetailInterface,
  CustomerInvoiceDataInterface,
  CustomerInvoiceTimeframesInterface,
  InputInvoiceDetailInterface,
  RecordInterface,
  sendInvoiceInterface
} from '../interfaces/InvoiceInterface';
import { captureException } from './logService';
import { rootStore } from '../mobx/rootStore';
import { WorkspaceModel } from '../mobx/models/workspace';

/**
 * @desc Invoice - Get invoice list
 * @param {WorkspaceModel} workspace - The current workspace context
 */
export const getInvoiceList = async (workspace: WorkspaceModel | null) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    let response;
    if (workspace?.customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS) {
      response = await axios.get(
        `${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceListByCustomer/${workspace?.customerId}`
      );
    } else if (workspace?.isAdmin || workspace?.isOwner) {
      response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceListByWorkspace/${workspace?.id}`);
    } else {
      dispatchInvoiceError("You don't have access for this page");
    }
    if (response) {
      const { data } = response;
      rootStore.invoiceStore.setInvoices(data);
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get invoice details please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Get invoice View Data
 * @param {string} item_id - ID of the invoice
 */
export const getInvoiceViewData = async (item_id: string) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      id: item_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceDetails`, { params });
    const { data } = response;
    if (data) {
      rootStore.invoiceStore.setInvoiceViewData(data);
    }
    return data;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get invoice View Data please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Get invoice activity detail Data
 * @param {string} item_id - ID of the invoice
 */
export const getInvoiceActivityDetailData = async (item_id: string, workspace: WorkspaceModel | null) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      invoiceId: item_id,
      isClient: workspace?.customerRole === CUSTOMER_MEMBER_ROLE.FULL_ACCESS
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceActivityList`, {
      params
    });
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get invoice activity details Data please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - create new invoice
 * @param {*}
 */
export const createNewInvoice = async (payload: InputInvoiceDetailInterface) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/invoice/postInvoice`, payload);
    if (response) {
      dispatchInvoiceSuccess(response.data.message);
      return response;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create new invoice please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - update invoice
 * @param {string} invoice_id - ID of the invoice to update
 */
export const updateInvoice = async (invoice_id: string, payload: InputInvoiceDetailInterface) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/invoice/putInvoice/${invoice_id}`, payload);
    const { data } = response;
    if (data) {
      dispatchInvoiceSuccess(response.data.message);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update invoice please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - get invoice id
 * @param {string} user_id - ID of the user
 * @param {boolean} isCurrency - Whether to update currency or not
 */
export const getInvoiceId = async (isCurrency: boolean) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getInvoiceId`);
    const { data } = response;
    if (data) {
      rootStore.invoiceStore.updateInvoiceDetail('number', data?.newInvoiceNumber || '');
      if (!isCurrency) {
        rootStore.invoiceStore.updateInvoiceDetail('currency', data?.lastCurrency || 'USD');
      }
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get invoice id please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Get Customer Invoice Type
 * @param {string} customer_id - ID of the customer
 */
export const getProjectHours = async (customer_id: string) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      customerId: customer_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getProjectHours`, { params });
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get project hours`;
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/** @desc Invoice - update record payment
 * @param {string} user_Id - ID of the user
 * @param {string} invoice_Id - ID of the invoice
 */
export const updateRecordPayment = async (invoice_Id: string, payload: RecordInterface) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      invoiceId: invoice_Id
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/invoice/putRecordPayment?`, payload, {
      params
    });
    const { data } = response;
    if (data) {
      dispatchInvoiceSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update record payment please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Get Customer Invoice Data
 * @param {*}
 */
export const getCustomerInvoiceData = async (customer_id: string | null) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      customerId: customer_id
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getCustomerWithInvoiceData`, {
      params
    });
    const { data } = response;
    if (data) {
      const updatedData = data?.map((item: CustomerInvoiceDataInterface) => {
        return { ...item, checked: true };
      });
      const userIds = updatedData?.map((data: { id: string }) => {
        return data?.id;
      });
      rootStore.invoiceStore.setCustomerInvoiceData(updatedData);
      rootStore.invoiceStore.updateBillableHoursDetail('projects', userIds);
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get Customer Invoice Data please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Get Customer Invoice Timeframes
 * @param {*}
 */
export const getCustomerInvoiceTimeframes = async () => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/invoice/getTimeFrames`);
    const { data } = response;
    if (data) {
      const updatelist = data?.map((item: CustomerInvoiceTimeframesInterface) => {
        return { text: item?.value, value: item?.text };
      });
      return updatelist;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get Customer Invoice Timeframes please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Update Track Time Invoice Details
 * @param {*}
 */
export const updateTrackTimeInvoiceDetails = async (payload: CustomerBillableHoursDetailInterface) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/invoice/putUninvoiced`, payload);
    const { data } = response;
    if (data) {
      const defaultItem = {
        amount: 0,
        desc: '',
        isTax: true,
        isTax2: true,
        itemType: 1,
        projectId: '',
        qty: 1,
        unitPrice: 0
      };
      rootStore.invoiceStore.updateInvoiceDetail('items', data?.length > 0 ? data : [defaultItem]);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Update track time invoice details please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - deleteActivityDetail
 * @param {string} invoice_Id - ID of the invoice.
 */
export const deleteActivityDetail = async (invoice_Id: string, payment_Id: string) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const params = {
      invoiceId: invoice_Id,
      deletePaymentId: payment_Id
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/invoice/deleteActivityDetails`, { params });
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete record payment details please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - send Invoice To Customers
 * @param {*}
 */
export const sendInvoiceToCustomers = async (payload: sendInvoiceInterface) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/invoice/sendInvoiceToCustomers`, payload);
    const { data } = response;
    if (data) {
      dispatchInvoiceSuccess(data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to send invoice to customers please try again`;
    captureException(error);
    dispatchInvoiceError(error);
    return undefined;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

/**
 * @desc Invoice - Delete Invoice
 * @param {string} invoice_Id - ID of the invoice to delete.
 */
export const deleteInvoice = async (invoice_Id: string) => {
  try {
    rootStore.invoiceStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/invoice/deleteInvoice/${invoice_Id}`);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete invoice please try again';
    captureException(error);
    dispatchInvoiceError(error);
    return false;
  } finally {
    rootStore.invoiceStore.setLoading(false);
  }
};

export function dispatchInvoiceError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

function dispatchInvoiceSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
